import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  CaptureSynthesis,
  SynthesisAction,
  SynthesisPoint,
  SynthesisTopic,
} from "./synthesis-schema";

type FetchOneResult = { ok: true; data: CaptureSynthesis | null } | { ok: false; error: string };
type PresenceResult = { ok: true; data: Set<string> } | { ok: false; error: string };
type PresenceOneResult = { ok: true; data: boolean } | { ok: false; error: string };

function mapPoint(raw: Record<string, unknown>): SynthesisPoint {
  return {
    text: String(raw.text ?? ""),
    itemIds: Array.isArray(raw.item_ids) ? raw.item_ids.map(String) : [],
  };
}
function mapAction(raw: Record<string, unknown>): SynthesisAction {
  return { ...mapPoint(raw), speaker: raw.speaker == null ? null : String(raw.speaker) };
}
function mapTopic(raw: Record<string, unknown>): SynthesisTopic {
  return {
    titre: String(raw.titre ?? ""),
    freins: Array.isArray(raw.freins)
      ? raw.freins.map((p) => mapPoint(p as Record<string, unknown>))
      : [],
    idees: Array.isArray(raw.idees)
      ? raw.idees.map((p) => mapPoint(p as Record<string, unknown>))
      : [],
  };
}
function mapRow(row: Record<string, unknown>): CaptureSynthesis {
  return {
    id: String(row.id),
    captureId: String(row.capture_id),
    promptVersion: String(row.prompt_version),
    model: row.model == null ? null : String(row.model),
    executiveSummary: String(row.executive_summary ?? ""),
    topics: Array.isArray(row.topics)
      ? row.topics.map((t) => mapTopic(t as Record<string, unknown>))
      : [],
    actions: Array.isArray(row.actions)
      ? row.actions.map((a) => mapAction(a as Record<string, unknown>))
      : [],
    excluded: Array.isArray(row.excluded)
      ? row.excluded.map((p) => mapPoint(p as Record<string, unknown>))
      : [],
    createdAt: String(row.created_at),
  };
}

// Dernière synthèse pour une capture (null si aucune → l'UI peut afficher un
// état « analyse en préparation »).
export async function fetchSynthesisForCapture(
  client: SupabaseClient,
  captureId: string,
): Promise<FetchOneResult> {
  const { data, error } = await client
    .from("capture_syntheses")
    // `excluded` (synthesis-v3) : la migration 20260727120000 DOIT être appliquée
    // avant le déploiement web, sinon PostgREST rejette la colonne inconnue et
    // tout l'onglet Sujets tombe.
    .select(
      "id, capture_id, prompt_version, model, executive_summary, topics, actions, excluded, created_at",
    )
    .eq("capture_id", captureId)
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) return { ok: false, error: error.message };
  const rows = (data ?? []) as Record<string, unknown>[];
  const first = rows[0];
  if (!first) return { ok: true, data: null };
  return { ok: true, data: mapRow(first) };
}

// Présence de synthèse pour PLUSIEURS captures en UNE requête (patron `.in()` de
// `fetchAuditsForCaptures` — évite le N+1 au mount de l'accueil). C'est le signal
// qui autorise le badge « Prêt » : tant qu'il est faux, la chaîne travaille encore
// (le cron `reconcile-extraction` rattrape audit puis synthèse, un étage par tic
// de 5 min). On ne sélectionne que `capture_id` — l'écran n'a besoin que du
// OUI/NON, jamais des topics ni du résumé.
//
// Volumétrie : une ligne par synthèse (≈ une par capture, plus une par
// ré-analyse), et l'accueil ne demande qu'une page de captures — on reste très
// loin du plafond de 1000 lignes de PostgREST.
export async function fetchSynthesisPresenceForCaptures(
  client: SupabaseClient,
  captureIds: string[],
): Promise<PresenceResult> {
  if (captureIds.length === 0) return { ok: true, data: new Set() };
  const { data, error } = await client
    .from("capture_syntheses")
    .select("capture_id")
    .in("capture_id", captureIds);
  if (error) return { ok: false, error: error.message };
  const rows = (data ?? []) as { capture_id: string }[];
  // Set : dédoublonne d'office les captures ré-analysées (plusieurs synthèses).
  return { ok: true, data: new Set(rows.map((r) => String(r.capture_id))) };
}

// Même signal pour UNE capture (page dédiée). Délègue au lot pour n'avoir qu'une
// seule requête à maintenir.
export async function fetchSynthesisPresence(
  client: SupabaseClient,
  captureId: string,
): Promise<PresenceOneResult> {
  const r = await fetchSynthesisPresenceForCaptures(client, [captureId]);
  if (!r.ok) return r;
  return { ok: true, data: r.data.has(captureId) };
}
